import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { DashboardActions } from './index';
import { catchError, debounceTime, exhaustMap, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { combineLatest, of } from 'rxjs';
import { ApiService } from '../../services/api.service';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { checkStaticData, DashboardState, selectMatchDetails, selectTalentDetails, selectVacancyDetails } from './dashboard.store';
import { HmVacanciesLoading, SetVacancyStatusSuccess } from './dashboard.actions';
import { DashboardProxy } from './dashboard.proxy';

@Injectable()
export class DashboardEffects {

  constructor(
    private actions$: Actions,
    private apiService: ApiService,
    private router: Router,
    private store: Store<DashboardState>,
    private dashboardProxy: DashboardProxy
  ) { }

  getStaticData$ = createEffect(() => this.actions$.pipe(
    ofType(DashboardActions.GetStaticData),
    withLatestFrom(this.store.select(checkStaticData)),
    switchMap(([action, dataLoaded]) => {
      if (dataLoaded) { return []; }
      return this.apiService.getStaticData(action.dataList).pipe(
        map(payload => DashboardActions.GetStaticDataSuccess({ payload })),
        catchError(() => of(DashboardActions.GetStaticDataFailure()))
      )}
    )
  ));

  getHmVacancies$ = createEffect(() => this.actions$.pipe(
    ofType(DashboardActions.GetHmVacancies),
    debounceTime(500),
    switchMap((action) => {
      this.store.dispatch(HmVacanciesLoading({ loading: true }))
      return this.apiService.getVacancies(action.searchParams).pipe(
        map(payload => {
          const loadMore = payload.vacancies && payload.vacancies.length === action.chunckSize;
          setTimeout(() => this.store.dispatch(HmVacanciesLoading({ loading: false })), 100);
          return DashboardActions.GetHmVacanciesSuccess({ payload, toPush: action.toPush, loadMore })
        }),
        // catchError(res => of(AuthActions.SignUpSuccess({ payload: { res, email: action.creds.email } }))
      );
    })
  ));

  setVacancyStatus$ = createEffect(() =>
  this.actions$.pipe(
    ofType(DashboardActions.SetVacancyStatus),
    tap(action => {
      this.apiService.setVacancyStatus(action.id, action.status, action.jobBoards).subscribe({
        next: (payload) => {
          this.store.dispatch(DashboardActions.SetVacancyStatusSuccess({ payload }));
        },
        error: (error) => {
          this.store.dispatch(DashboardActions.SetVacancyStatusFailure({ error }));
        }
      });
    })
  ),{ dispatch: false });

  cloneVacancy$ = createEffect(() => this.actions$.pipe(
    ofType(DashboardActions.CloneVacancy),
    switchMap((action) => this.apiService.cloneVacancy(action.id).pipe(
      map(id => DashboardActions.CloneVacancySuccess({ id })),
      // catchError(res => of(DashboardActions.GetHmVacanciesSuccess({ payload: res })))
    ))
  ));

  getMatchById$ = createEffect(() => this.actions$.pipe(
    ofType(DashboardActions.GetMatchById),
    withLatestFrom(this.store.select(selectMatchDetails)),
    switchMap(([action, current]) => {
      // if (current && current.id === action.id) {
      //   return of(DashboardActions.AlreadyLoaded());
      // }
      return this.apiService.showMatchById(action.id).pipe(
        map(payload => {
          return DashboardActions.GetMatchByIdSuccess({ payload })
        }),
      // catchError(res => of(DashboardActions.GetHmVacanciesSuccess({ payload: res })))
    )})
  ));

  getVacancyById$ = createEffect(() => this.actions$.pipe(
    ofType(DashboardActions.GetVacancyById),
    withLatestFrom(this.store.select(selectVacancyDetails)),
    switchMap(([action, current]) => {
      // if (current && current.id === action.id) {
      //   return of(DashboardActions.AlreadyLoaded());
      // }
      return this.apiService.showVacancyById(action.id).pipe(
        map(payload => DashboardActions.GetVacancyByIdSuccess({ payload }))
        // catchError(res => of(AuthActions.SignUpSuccess({ payload: { res, email: action.creds.email } }))
      )}
    )
  ));

  getTalentById$ = createEffect(() => this.actions$.pipe(
    ofType(DashboardActions.GetTalentById),
    withLatestFrom(this.store.select(selectTalentDetails)),
    switchMap(([action, current]) => {
      // if (current && current.id === action.id) {
      //   return of(DashboardActions.AlreadyLoaded());
      // }
      return this.apiService.showTalentById(action.id, action.vacancyId).pipe(
        tap(payload => console.log('Talent API Response:', payload)),
        map(payload => DashboardActions.GetTalentByIdSuccess({ payload: { ...payload, id: action.id } }))
        // catchError(res => of(AuthActions.SignUpSuccess({ payload: { res, email: action.creds.email } }))
      )}
    )
  ));

matchRecalculation$ = createEffect(() => this.actions$.pipe(
  ofType(DashboardActions.StartMatchCalculation),
  switchMap((action) => {
    if (action.skipApi) {
      return of(DashboardActions.EndMatchCalculation());
    }
    return this.apiService.recalculateMatch(action.vacancyId).pipe(
      map(() => {
        this.dashboardProxy.talentWasChanged$.next(false); 
        return DashboardActions.EndMatchCalculation();
      }),
      catchError(err => {
        this.dashboardProxy.talentWasChanged$.next(false); 
        return of(DashboardActions.EndMatchCalculation());
      })
    );
  })
));




  adjustVacancySkills$ = createEffect(() => this.actions$.pipe(
    ofType(DashboardActions.AdjustHardSoftSkills),
    exhaustMap((action) => {
      const methods = [];
      if (action.hardSkills) {
        methods.push(this.apiService.setVacancyHardSkills(action.vacancyId, action.hardSkills));
      }
      if (action.softSkills) {
        methods.push(this.apiService.setVacancySoftSkills(action.vacancyId, action.softSkills));
      }
      return combineLatest(methods).pipe(
        map(() => DashboardActions.AdjustHardSoftSkillsSuccess()),
        catchError((err) => of(DashboardActions.AdjustHardSoftSkillsSuccess()))
      );
    })
  ));

}
